import { GiftCode, Product } from '../types';

/**
 * Normalizes gift code length: converts 13 months to 12 months
 * This is due to legacy system including an extra month for corrections
 * @param months - Original gift code length in months
 * @returns Normalized length (13 -> 12, otherwise unchanged)
 */
export const normalizeGiftCodeLength = (months: number): number => {
  return months === 13 ? 12 : months;
};

/**
 * Formats the duration of a gift code into a human-readable string
 * @param months - Number of months in the gift code (will be normalized: 13 -> 12)
 * @returns Formatted duration string (e.g., "6 months", "1 year", "2 years 3 months")
 */
export const formatGiftCodeDuration = (months: number): string => {
  // Normalize 13 months to 12 months
  const normalizedMonths = normalizeGiftCodeLength(months);
  
  if (normalizedMonths >= 12) {
    const years = Math.floor(normalizedMonths / 12);
    const remainingMonths = normalizedMonths % 12;
    if (remainingMonths === 0) {
      return years === 1 ? '1 year' : `${years} years`;
    }
    return `${years} year${years > 1 ? 's' : ''} ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
  }
  return `${normalizedMonths} month${normalizedMonths > 1 ? 's' : ''}`;
};

/**
 * Creates a consistent gift membership message showing duration and product name
 * @param giftCode - The applied gift code
 * @param product - The selected product/membership
 * @returns Formatted message string (e.g., "6 months of free All Access Membership included")
 */
export const formatGiftMembershipMessage = (
  giftCode: GiftCode, 
  product: Product | null
): string => {
  const duration = formatGiftCodeDuration(giftCode.length);
  const membershipName = product?.name || 'membership';
  return `${duration} of free ${membershipName} included`;
};
